<template>
	<BaseModal :show="show" border-classes="border-t-6 border-secondary-light">
		<form class="flex flex-col h-fit sm:w-[600px] md:w-[745px] md:max-w-[745px] bg-neutral">
			<div class="flex flex-row align-middle">
				<p class="text-xl font-bold leading-7 text-heading">Manage User Permissions</p>
				<img
					src="@/assets/images/icon/dismiss.svg"
					alt="Dismiss"
					title="Dismiss"
					class="ml-auto cursor-pointer h-4.5 w-4.5"
					@click="cancel"
				/>
			</div>

			<p class="mt-6 md:mt-2 font-medium leading-6 text-grayscale-body">
				for <span class="clickable-text" @click="onOrganizationClicked(permissionToEdit?.id)">{{ permissionToEdit?.organization_name + (permissionToEdit?.organization_name_dba ? " (" + permissionToEdit?.organization_name_dba + ")" : "") }}</span> {{ permissionToEdit?.deleted_at ? '(Inactive)' : '' }}
			</p>

			<InputText
				v-if="login_status"
				class="w-[calc(50%-(theme(spacing.5)/2))] mt-2"
				type="text"
				label-text-size="text-sm"
				input-height="h-10"
				font-weight="font-normal"
				v-model="login_status"
				:disabled="true"
			/>

			<div class="border border-neutral w-full h-[1px] my-4"></div>

			<div class="flex flex-col">
				<div class="flex flex-col space-x-0 md:flex-row md:space-x-5">
					<InputText
						class="userpilot-mask flex-1"
						label="Email"
						type="text"
						label-text-size="text-sm"
						input-height="h-11"
						font-weight="font-normal"
						v-model="email"
						:disabled="true"
					/>

					<InputText
						class="userpilot-mask flex-1"
						label="Phone"
						label-text-size="text-sm"
						input-height="h-11"
						font-weight="font-normal"
						v-model="phone_number"
						v-maska:phone_number_unmasked.unmasked="'(###) ###-####'"
						:disabled="true"
					/>
				</div>

				<div class="flex flex-col space-x-0 md:flex-row md:space-x-5">
					<InputText
						class="userpilot-mask flex-1"
						label="First name"
						type="text"
						label-text-size="text-sm"
						input-height="h-11"
						font-weight="font-normal"
						v-model="first_name"
						:disabled="true"
					/>

					<InputText
						class="userpilot-mask flex-1"
						label="Last name"
						type="text"
						label-text-size="text-sm"
						input-height="h-11"
						font-weight="font-normal"
						v-model="last_name"
						:disabled="true"
					/>
				</div>

				<div class="flex flex-col space-x-0 md:flex-row md:space-x-5 mb-6">
					<ListBox
						id="combo_user_role2"
						v-model="edit_user_role"
						:items="organizationRoles"
						label="User role"
						label-text-size="text-sm"
						placeholder="Select user role"
						class="w-[calc(50%-(theme(spacing.5)/2))]"
						:show-options-on-top="true"
						:errors="error"
						error-field="user.role"
						input-height="h-11"
						:disabled="!canChangeRole"
						:showListBoxOutline="true"
					/>

					<Combobox
						id="combo_point_of_contact"
						v-if="points_of_contact.length > 1"
						v-model="selected_point_of_contact"
						:items="points_of_contact"
						title="Point of contact"
						placeholder="Select point of contact"
						class="userpilot-mask flex-1"
						:show-options-on-top="true"
						:errors="error"
						error-field="user.point_of_contact"
						:enterable="false"
						:readonly="true"
						input-height="h-11"
						label-text-size="text-sm"
						:disabled="!is_active"
					/>
				</div>
			</div>

			<div class="border border-neutral w-full h-[1px] mt-1 mb-5"></div>

			<div class="flex w-full flex-col-reverse md:flex-row">
				<!--<div class="md:mr-auto mt-1 md:mt-0"></div> remove if showing Deactivate -->
			<!--
				Deactivate Org permission button.
			<SubmitButton
					:label="is_active ? 'Deactivate' : 'Activate'"
					:type="ButtonType.Warning"
					:size="ButtonSize.Small"
					:disabled="isSaving"
					class="md:mr-auto mt-1 md:mt-0"
					:loading="isDeactivating"
					@click="toggleActivatePermission"
				/>-->
				<SubmitButton
					label="Delete"
					:type="ButtonType.Warning"
					:size="ButtonSize.Small"
					:disabled="isSaving"
					class="md:mr-auto mt-1 md:mt-0"
					:loading="showDeleteUserOrgModal"
					@click="onDeleteUserOrg"
				/>
				<SubmitButton
					:label="is_active ? 'Cancel' : 'Close'"
					:type="ButtonType.Outline"
					:size="ButtonSize.Small"
					:disabled="isSaving"
					class="md:mr-3 mt-1 md:mt-0"
					@click="cancel"
				/>
				<SubmitButton
					label="Save"
					:size="ButtonSize.Small"
					:loading="isSaving"
					@click="saveClicked"
					:disabled="!is_active"
				/>
			</div>
		</form>
	</BaseModal>

	<Teleport to="html">
		<MultiButtonModal
			:show="showDeleteUserOrgModal"
			icon="alert"
			title="Delete User Organization"
			message="Are you sure?"
			:buttons="[
							new ModalButtonData(
								'Cancel', () => { showDeleteUserOrgModal = false }, ButtonType.Default, ButtonSize.Small
							),
							new ModalButtonData(
								'Delete', deleteUserOrganization, ButtonType.Default, ButtonSize.Small
							)
						]"
		/>
	</Teleport>
</template>

<script setup lang="js">
import InputText from '@/components/Forms/InputText.vue'
import { computed, ref, watch } from 'vue'
import { vMaska } from 'maska/vue'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { ButtonSize, ButtonType } from '@/components/Forms/Buttons/SubmitButtonConstants.js'
import Combobox from '@/components/Forms/Combobox.vue'
import { userOrganizationsApi } from '@/api/Organizations/UserOrganizationsApi.js'
import router from '@/router/router.js'
import ListBox from '@/components/Forms/ListBox.vue'
import { useAuthStore } from '@/stores/authStore.js'
import BaseModal from '@/components/Alerts/BaseModal.vue'
import { ModalButtonData } from '@/components/Alerts/ModalButtonData.js'
import MultiButtonModal from '@/components/Alerts/MultiButtonModal.vue'
import { userApi } from '@/api/User/UserApi.js'
import { Toaster } from '@/models/utils/Toaster.js'

const props = defineProps({
	show: { type: Boolean, default: false },
	permissionToEdit: { type: Object, default: null },
	pointsOfContact: { type: Map, default: null },
	orgRoles: { type: Array, default: null }
})

const isSaving = ref(false)
//const isDeactivating = ref(false)
const error = ref(null)
const login_status = ref(null)
const email = ref(null)
const phone_number = ref(null)
const phone_number_unmasked = ref(null)
const first_name = ref(null)
const last_name = ref(null)
const points_of_contact = ref([])
const selected_point_of_contact = ref(null)
const is_active = ref(true)
const old_active_state = ref(null)
const authStore = useAuthStore()
const user = authStore?.getAuth?.user()
const showDeleteUserOrgModal = ref(false)
const organizationRoles = ref([])

/**
 *
 * @type {Ref<UnwrapRef<{id: number, text: string, disabled: boolean, role: OrganizationRole}>>}
 */
const edit_user_role = ref({ id: 0, text: 'Select Role', disabled: false, role: null })
const edit_role_hierarchy = ref(0)//Original hierarchy of the role that permission being edited
const current_user_role = ref(null)//Role of the current user
const toaster = new Toaster()

/**
 * Check if user can modify the role.
 * A portal admin can change anything.
 * A user who is not main admin cannot set a set to one, or remove it from a user.
 * @type @vue-computed {boolean}
 */
const canChangeRole = computed(()=>
{
	if (!props.permissionToEdit) return false;
	if (is_active.value)
	{
		//Portal admins can do anything
		if (authStore.isPortalAdmin)
		{
			return true;
		}

		//Otherwise, check if they can
		const role_for_org = authStore.getUserRoleForOrg(props.permissionToEdit.id)

		//If the user's role hierarchy is less than the currently selected value, don't let the user change it.
		if (role_for_org.hierarchy >= parseFloat(edit_user_role.value.role.hierarchy))
		{
			return true;
		}
	}
	return false
})
defineExpose({ phone_number_unmasked })

const emit = defineEmits(['dismiss', 'showDiscardChangesModal', 'showToggleActivatePermissionModal', 'showDeleteUserOrgModal'])

watch(
	() => props.pointsOfContact,
	(newValue) =>
	{
		if (newValue)
		{
			handlePointsOfContact(newValue)
		}
	}
)

watch(
	() => props.permissionToEdit,
	(newValue) =>
	{
		if (newValue)
		{
			handleRoles();
			handlePermission(props.permissionToEdit)
			handlePointsOfContact(props.pointsOfContact)

		}
	}
)

/**
 * Gets the initially selected role
 * @param p_role_id
 * @returns {{id: number, text: string, disabled: boolean, role: null}|*}
 */
function getInitialRole(p_role_id)
{
	const role = organizationRoles.value.find(p_role => p_role.id === p_role_id);
	if (!p_role_id || p_role_id <= 0 || !role)
	{
		return { id: 0, text: 'Select Role', disabled: false, role: null };
	}

	return role;
}

/**
 * Gets the hierarchy for a specific role
 * @param p_role_id
 * @returns {number}
 */
function getRoleHierarchy(p_role_id)
{
	const role = props.orgRoles.find(p_role => p_role.id === p_role_id);

	if (role)
	{
		return parseFloat(role.role.hierarchy);
	}

	return 0;
}

function findMapKeyByValue(targetValue, targetMap)
{
	if (!targetMap)
	{
		return null
	}

	for (const [key, value] of targetMap.entries())
	{
		if (value === targetValue)
		{
			return key
		}
	}
	return null
}

/**
 * Setup roles drop down based on permissions
 */
function handleRoles()
{
	current_user_role.value = authStore.getUserRole

	if (!current_user_role.value)
	{
		toaster.error("An error occurred. Please try reloading the page.")
	}
	const role_for_org = authStore.getUserRoleForOrg(props.permissionToEdit.id)

	const roles = props.orgRoles;
	organizationRoles.value = [];
	for (const role of roles)
	{
		let disabled = true;

		if (authStore.isPortalAdmin || parseFloat(role.hierarchy) <= role_for_org.hierarchy)
		{
			disabled = false;
		}

		organizationRoles.value.push( { id: role.id, text: role.role, disabled: disabled, role: role })
	}
}

/**
 * Load the permission related data.
 * @param permission
 */
function handlePermission(permission)
{
	if (permission)
	{
		//Remove current user so they can set themselves as POC for their orgs
		props.pointsOfContact.delete(permission.user_id)

		email.value = permission.email
		phone_number.value = permission.phone_number
		first_name.value = permission.given_name
		last_name.value = permission.family_name
		is_active.value = permission.user_organization_deleted_at == null
		//selected_user_role.value = roles.get(permission.user_role)

		if (old_active_state.value == null)
		{
			old_active_state.value = is_active.value
		}

		//If the user's role hierarchy is less than the currently selected value, don't let the user change it.
		edit_role_hierarchy.value = getRoleHierarchy(permission.user_role_id)
		if (permission.user_role_id > 0)
		{
			edit_user_role.value = getInitialRole(permission.user_role_id);
		}
	}
}

/**
 * Load points of contacts. handlePermission removes current user from list.
 * @param pointsOfContact
 */
function handlePointsOfContact(pointsOfContact)
{
	if (pointsOfContact)
	{
		points_of_contact.value = Array.from(pointsOfContact.values())
		selected_point_of_contact.value = props.permissionToEdit?.point_of_contact_id ? pointsOfContact.get(parseInt(props.permissionToEdit.point_of_contact_id)) : null
	}
}

function cancel()
{
	if (changesMade())
	{
		emit('showDiscardChangesModal', (discardChanges) =>
		{
			if (discardChanges)
			{
				emit('dismiss', didActiveStateChange())
			}
		})
	}
	else
	{
		emit('dismiss', didActiveStateChange())
	}
}

function changesMade()
{
	let oldRole = props.permissionToEdit["user_role_id"]
	let selectedRole = edit_user_role.value["id"]
	let oldPocId = props.permissionToEdit?.point_of_contact_id ? parseInt(props.permissionToEdit?.point_of_contact_id) : null
	let selectedPocId = findMapKeyByValue(selected_point_of_contact.value, props.pointsOfContact)

	return (
		(selectedRole != null && oldRole !== selectedRole) || (oldPocId !== selectedPocId)
	)
}

/*function toggleActivatePermission()
{
	if (is_active.value)
	{
		deactivate()
	}
	else
	{
		activate()
	}
}

function activate()
{
	emit('showToggleActivatePermissionModal', true, (toggleActivatePermission) =>
	{
		if (toggleActivatePermission)
		{
			error.value = null
			isDeactivating.value = true

			userOrganizationsApi.restore(
				props.permissionToEdit?.user_organization_id,
				() =>
				{
					is_active.value = true
					isDeactivating.value = false
				},
				(e) =>
				{
					isDeactivating.value = false
					error.value = e.showErrorBanner()
				}
			)
		}
	})
}

function deactivate()
{
	emit('showToggleActivatePermissionModal', false, (toggleActivatePermission) =>
	{
		if (toggleActivatePermission)
		{
			error.value = null
			isDeactivating.value = true

			userOrganizationsApi.delete(
				props.permissionToEdit?.user_organization_id,
				() =>
				{
					updateCurrentUser()
					is_active.value = false
					isDeactivating.value = false
				},
				(e) =>
				{
					isDeactivating.value = false
					error.value = e.showErrorBanner()
				}
			)
		}
	})
}*/

/**
 * Show the delete user organization confirmation dialog
 */
function onDeleteUserOrg()
{
	showDeleteUserOrgModal.value = true
}

/**
 * Permanently Deletes a permission from a user
 */
function deleteUserOrganization()
{
	error.value = null

	userOrganizationsApi.permDelete(
		props.permissionToEdit?.user_organization_id,
		() =>
		{
			updateCurrentUser()
			is_active.value = false
			showDeleteUserOrgModal.value = false
			emit('dismiss', true)
		},
		(e) =>
		{
			showDeleteUserOrgModal.value = false
			error.value = e.showErrorBanner()
		}
	)
}

function saveClicked()
{
	if (!changesMade())
	{
		cancel()
		return
	}

	error.value = null
	isSaving.value = true

	userOrganizationsApi.update(
		{
			'user_organization': {
				'id': props.permissionToEdit?.user_organization_id,
				'organization_role_id': edit_user_role.value["id"],//findMapKeyByValue(selected_user_role.value, roles),
				'organization_title': props.permissionToEdit?.user_title,
				'point_of_contact_id': findMapKeyByValue(selected_point_of_contact.value, props.pointsOfContact)
			}
		},
		() =>
		{
			//Refresh current user's data if they changed their own
			updateCurrentUser()

			isSaving.value = false
			emit('dismiss', true)
		},
		(e) =>
		{
			isSaving.value = false
			error.value = e.showErrorBanner()
		}
	)
}

function didActiveStateChange()
{
	return old_active_state.value !== is_active.value
}

function onOrganizationClicked(id) {
	if (changesMade())
	{
		emit('showDiscardChangesModal', (discardChanges) =>
		{
			if (discardChanges)
			{
				router.push({ name: 'admin.organizations', query: { show_org_id: id } })
			}
		})
	}
	else
	{
		router.push({ name: 'admin.organizations', query: { show_org_id: id } })
	}
}

/**
 * Refresh the current user's data if they changed their own settings.
 */
function updateCurrentUser()
{
	//Refresh current user's data if they changed their own
	if (user.id === props.permissionToEdit.user_id)
	{
		userApi.refreshUserData()
	}
}
</script>